import { useEffect, useMemo, useState } from "react";
import apiClient from "@/helpers/api.helper";
import { STUDENT_GET_SCHOOL_CLASSES } from "@/constants/urls";
import type { StudentSchoolClass } from "../types";

/**
 * Grade / Class options for the sitting section - the same list, with the
 * same names, as the Enquiry form's grade dropdown: the backend class Name
 * ("Year 7"), first occurrence per name so sections collapse into one entry.
 * No hard-coded fallback: the paper form's Grade/Class is optional, so an
 * empty list while classes load is fine.
 */
export function useSchoolClassOptions() {
  const [classes, setClasses] = useState<StudentSchoolClass[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchClasses = async () => {
      try {
        const data = await apiClient.get<StudentSchoolClass[]>(STUDENT_GET_SCHOOL_CLASSES);
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setClasses(data);
        }
      } catch {
        // Grade/Class is optional on the paper form, so an empty list is acceptable.
      }
    };
    fetchClasses();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const uniqueByName = new Map<string, { label: string; value: string }>();
    classes
      .filter((c) => c.isActive)
      .sort((a, b) => a.number - b.number)
      .forEach((c) => {
        if (!uniqueByName.has(c.name)) {
          uniqueByName.set(c.name, { label: c.name, value: c.name });
        }
      });
    return Array.from(uniqueByName.values());
  }, [classes]);
}
